import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Store } from '@ngxs/store';
import { TeamNavigationService } from 'app/library/team/services/team-navigation.service';
import { SelectTeamsDialogComponent } from 'app/main/pages/teams/dialogs/select-teams-dialog/select-teams-dialog.component';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { SimpleEntity } from 'app/projects/shared/src/lib/models/simple-entity.model';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { PerformanceProfilePersonalReview } from 'app/library/performance-profile-personal-review/models/performance-profile-personal-review.model';
import { DeletePerformanceTeamReviewDialog } from 'app/library/performance-team-review/dialogs/delete-performance-team-review-dialog/delete-performance-team-review-dialog.component';
import { PerformanceTeamReviewDialog } from 'app/library/performance-team-review/dialogs/performance-team-review-dialog/performance-team-review-dialog.component';
import { IPerformanceTeamReviewActionPayload } from 'app/library/performance-team-review/interfaces/create-performance-team-review.interface';
import { PerformanceTeamReviewStatus } from 'app/library/performance-team-review/models/performance-team-review-status';
import { PerformanceTeamReview } from 'app/library/performance-team-review/models/performance-team-review.model';
import { PerformanceTeamReviewService } from 'app/library/performance-team-review/services/performance-team-review.service';
import { PreformanceReviewsDataSource } from './datasource';

@Component({
    selector: 'app-performance-team-reviews-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PerformanceTeamReviewsListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @HostListener('mouseenter')
    onMouseEnter(e: Event) {
        if (e) {
            this.isActionBoxHovered = true;
        }
    }

    @HostListener('mouseleave')
    onMouseLeave(e: Event) {
        if (e) {
            this.isActionBoxHovered = false;
        }
    }

    @Input()
    filteredPerformanceTeamReviews$: Observable<PerformanceTeamReview[]>;

    @Input()
    performanceTeamReviewsStateSearchTerm$: Observable<string>;
    performanceTeamReviewsStateSearchTerm: string;

    @Input()
    isFetchingPerformanceTeamReviewsList$: Observable<boolean>;

    @Input()
    performanceTeamReviewsStateTotalCount$: Observable<number>;

    @Input()
    performanceTeamReviewTypeInput: FormControl;

    @Input()
    performanceTeamPeriodInput: FormControl;

    @Input()
    loadPerformanceTeamReviews$: Observable<boolean>;

    @Input()
    performanceTeamTemplateTypes: SimpleEntity[];

    @Input()
    teamId: string;

    @Input()
    displayedColumns: string[] = [];

    @Input()
    performanceProfilePersonalReview: PerformanceProfilePersonalReview;

    @Input()
    isViewOnly: boolean = false;

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    deletePerformanceTeamReviewDialogRef: MatDialogRef<DeletePerformanceTeamReviewDialog>;
    selectTeamsDialogRef: MatDialogRef<SelectTeamsDialogComponent>;

    dataSource: PreformanceReviewsDataSource;
    performanceTeamReviewStatus = PerformanceTeamReviewStatus;
    isActionBoxHovered: boolean = false;
    selectedPerformanceTeamReviewTypeId: string;
    selectedPerformancePeriod: string;

    isCreatedFromAdminSpace = 'created_from_admin_space_translation_key';
    selectedNode: PerformanceTeamReview;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    @Output()
    selectedReview = new EventEmitter<PerformanceTeamReview>();

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _router: Router,
        private _matDialog: MatDialog,
        private _performanceTeamReviewService: PerformanceTeamReviewService,
        private _teamNavigationService: TeamNavigationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.dataSource = new PreformanceReviewsDataSource(this.filteredPerformanceTeamReviews$, this._store, this._action$);
    }

    ngAfterViewInit(): void {
        this.performanceTeamReviewsStateSearchTerm$
            .pipe(
                filter((searchTerm, index) => {
                    this.performanceTeamReviewsStateSearchTerm = searchTerm || '';
                    return index > 0;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.loadPerformanceTeamReviewsPage(true);
            });

        this.loadPerformanceTeamReviews$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadPerformanceTeamReviewsPage(resetPageIndex);
            });

        this.performanceTeamReviewTypeInput?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((performanceTeamReviewType: string) => {
            this.selectedPerformanceTeamReviewTypeId = performanceTeamReviewType;
            this.loadPerformanceTeamReviewsPage();
        });

        this.performanceTeamPeriodInput?.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((performancePeriod: string) => {
            this.selectedPerformancePeriod = performancePeriod;
            this.loadPerformanceTeamReviewsPage();
        });

        this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
            const itemsleft = length - pageIndex * pageSize;
            const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
            // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
            const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
            const contentElementHeight = document.getElementById('container-2').offsetHeight;
            const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

            setTimeout(() => {
                document.getElementById('container-3').scrollTop = scrollTop;
            });
        });

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadPerformanceTeamReviewsPage(false);
        });
    }

    openPerformanceTeamReview(performanceTeamReview: PerformanceTeamReview): void {
        this._router.navigate([`admin/people-development/organization-review/review/${performanceTeamReview.id}`]);
    }

    deletePerformanceTeamReview(performanceTeamReview: PerformanceTeamReview): void {
        this.deletePerformanceTeamReviewDialogRef = this._matDialog.open(DeletePerformanceTeamReviewDialog, {
            disableClose: false,
            width: '600px',
        });

        this.deletePerformanceTeamReviewDialogRef.componentInstance.performanceTeamReview = performanceTeamReview;

        this.deletePerformanceTeamReviewDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const isRemoved = await this._performanceTeamReviewService.deletePerformanceTeamReview(performanceTeamReview.id);
                if (isRemoved) {
                    this.loadPerformanceTeamReviewsPage(true);
                }
            }

            this.deletePerformanceTeamReviewDialogRef = null;
        });
    }

    openEditPerformanceTeamReviewDialog(performanceTeamReview: PerformanceTeamReview): void {
        const dialogRef = this._matDialog.open(PerformanceTeamReviewDialog, {
            disableClose: false,
            width: '632px',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = 'PERFORMANCE_REVIEW.PERFORMANCE_REVIEWS.EDIT_PERFORMANCE_REVIEW';
        dialogRef.componentInstance.performanceTeamTemplateTypes = this.performanceTeamTemplateTypes;
        dialogRef.componentInstance.performanceTeamReview = performanceTeamReview;

        dialogRef.afterClosed().subscribe(async (updatedPerformanceTeamReview: IPerformanceTeamReviewActionPayload) => {
            if (updatedPerformanceTeamReview) {
                const isUpdated = await this._performanceTeamReviewService.updatePerformanceTeamReview(performanceTeamReview.id, updatedPerformanceTeamReview);
                if (isUpdated) {
                    this.loadPerformanceTeamReviewsPage(false);
                }
            }
        });
    }

    openPerformanceTeamReviewDetailsPage(performanceTeamReview: PerformanceTeamReview): void {
        this._teamNavigationService.addTeamPath(window.location.pathname);
        this._store.dispatch(new Navigate([`my-teams/team/${this.teamId}/team-reviews/${performanceTeamReview.id}`]));
    }

    onSelect(node: PerformanceTeamReview): void {
        this.selectedNode = node;
        this.selectedReview.emit(node);
    }

    onDeselect(node: PerformanceTeamReview): void {
        this.selectedNode = null;
        this.selectedReview.emit(null);
    }

    loadPerformanceTeamReviewsPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadPerformanceTeamReviews(
            this.teamId,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            this.performanceTeamReviewsStateSearchTerm,
            this.performanceProfilePersonalReview ? this.performanceProfilePersonalReview.type.id : this.selectedPerformanceTeamReviewTypeId,
            this.performanceProfilePersonalReview ? this.performanceProfilePersonalReview.periodUnderReview.id : this.selectedPerformancePeriod,
            this.performanceProfilePersonalReview ? this.performanceProfilePersonalReview.goalSettingPeriod.id : null
        );
    }
}
